/**
 * bulkAction/BulkAssignModalWithScope.jsx
 *
 * Scoped KPI question assignment modal with Tabbed UI:
 *   1. Assign by Location (Location-first: Select locations -> filter users -> unique mapping)
 *   2. Assign by User (User-first: Select user -> select subset of their locations)
 *
 * Props:
 *   show                {boolean}
 *   onHide              {function}
 *   selectedIds         {number[]}  — checked KPI question IDs
 *   financialYearId     {number}
 *   locations           {array}     — [{id, name, location}] from useKpiData
 *   selectedKpis        {array}     — [{id, kpi_name}] checked KPI rows, for labels in the Remove tab
 *   onSuccess           {function}  — called after successful save
 */
import { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { apiCall } from "../../../../../../_services/apiCall";
import config from "../../../../../../config/config";
import MultiSelectDropdown from "../../../../../../globalComponents/form/dropdowns/MultiSelectDropdown";
import OwnerAssignedKpisDrawer from "./OwnerAssignedKpisDrawer";

/* ════════════════════════════════════════════════
   DESIGN TOKENS — mirrors BulkAuditAssignModal
════════════════════════════════════════════════ */
const T = {
  blue: "#2563eb", blueL: "#eff6ff", blueB: "#bfdbfe",
  green: "#15803d", greenL: "#f0fdf4", greenB: "#bbf7d0",
  amber: "#b45309", amberL: "#fffbeb", amberB: "#fde68a",
  red: "#be123c", redL: "#fff1f2", redB: "#fecdd3",
  ink: "#0f172a", ink2: "#334155", ink3: "#64748b", ink4: "#94a3b8",
  rule: "#e2e8f0", rule2: "#f1f5f9", bg: "#f8fafc",
  white: "#ffffff",
};

/* ════════════════════════════════════════════════
   ATOMS
════════════════════════════════════════════════ */
const Spin = ({ size = 13, color = T.blue }) => (
  <span style={{ display: "inline-block", width: size, height: size, flexShrink: 0, border: `2px solid ${color}22`, borderTopColor: color, borderRadius: "50%", animation: "boa-spin .6s linear infinite" }} />
);

const StepBadge = ({ n, done, active }) => (
  <div style={{ width: 22, height: 22, borderRadius: "50%", flexShrink: 0, background: done ? T.green : active ? T.blue : T.rule, color: T.white, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 9.5, fontWeight: 800, transition: "background .2s" }}>
    {done ? "✓" : n}
  </div>
);

const StepRow = ({ n, label, done, active }) => (
  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
    <StepBadge n={n} done={done} active={active} />
    <span style={{ fontSize: 12.5, fontWeight: 700, color: done || active ? T.ink : T.ink4 }}>{label}</span>
  </div>
);

const BulkOwnerAssignModal = ({
  show,
  onHide,
  selectedIds = [],
  financialYearId,
  locations = [],
  selectedKpis = [],
  allQuestions = [],
  skippedDerivedCount = 0,
  onSuccess,
}) => {
  const isMounted = useRef(true);
  useEffect(() => () => { isMounted.current = false; }, []);

  const [activeTab, setActiveTab] = useState("location"); // "location" | "user"
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  // Tab 1 (Location-First) state
  const [selectedLocationsScope, setSelectedLocationsScope] = useState([]); // [{id, name}]
  const [userPool, setUserPool] = useState([]); // list of filtered sub-users
  const [locationUserMap, setLocationUserMap] = useState({}); // { locationId: [userId] }
  const [loadingUsers, setLoadingUsers] = useState(false);

  // Tab 2 (User-First) state
  const [allDataOwners, setAllDataOwners] = useState([]);
  const [selectedOwnerIds, setSelectedOwnerIds] = useState([]); // [userId]
  const [ownerLocationMap, setOwnerLocationMap] = useState({}); // { userId: [locationId] }

  const [dbAssignments, setDbAssignments] = useState([]);
  const [loadingDbAssignments, setLoadingDbAssignments] = useState(false);

  const [drawer, setDrawer] = useState(null);

  const fetchDbAssignments = async () => {
    if (!selectedIds.length || !financialYearId) return;
    setLoadingDbAssignments(true);
    try {
      const { isSuccess, data } = await apiCall(
        `${config.POSTLOGIN_API_URL_COMPANY}quantative-data/assigned-kpis`,
        {},
        {
          kpiIds: selectedIds.join(","),
          financialYearId,
        },
        "GET"
      );
      if (isSuccess && isMounted.current) {
        setDbAssignments(data?.data || []);
      }
    } catch (e) {
      console.error("Failed to fetch existing assignments", e);
    } finally {
      if (isMounted.current) setLoadingDbAssignments(false);
    }
  };

  /**
   * Label for one scope option. `name` already reads KMC-T1 for a sub-location,
   * so it wins over unitCode — which holds the parent's code (KMC) and would
   * otherwise render every sub-location of a site identically.
   */
  const scopeLabel = useCallback((loc) => (
    loc?.name
    || loc?.location?.unitCode
    || loc?.unitCode
    || loc?.location?.name
    || `Location ${loc?.id}`
  ), []);

  /**
   * Turns a picker id into the scope the assignment API expects. Sub-location
   * options are keyed `sub_<id>` by useKpiData, and a split site is only ever
   * offered as its sub-locations, so KMC-T1 assigns to KMC + T1.
   */
  const scopeOf = useCallback((pickerId) => {
    const match = locations.find((l) => String(l.id) === String(pickerId));
    if (match?.locationId) {
      return { locationId: Number(match.locationId), subLocationId: match.subLocationId ?? null };
    }
    return { locationId: Number(pickerId), subLocationId: null };
  }, [locations]);

  // Reset modal state on open
  useEffect(() => {
    if (!show) return;
    setError("");
    setActiveTab("location");
    setSelectedLocationsScope([]);
    setUserPool([]);
    setLocationUserMap({});
    setSelectedOwnerIds([]);
    setOwnerLocationMap({});
    setDbAssignments([]);
    setDrawer(null);
    fetchAllDataOwners();
    fetchDbAssignments();
  }, [show]);

  // Fetch all data owners for Tab 2
  const fetchAllDataOwners = async () => {
    setLoadingUsers(true);
    try {
      const { isSuccess, data } = await apiCall(
        `${config.POSTLOGIN_API_URL_COMPANY}getSubUser`,
        {},
        {},
        "GET"
      );
      if (isSuccess && isMounted.current) {
        const filtered = (data?.data || []).filter(
          (u) => {
            const roleLower = u.role?.toLowerCase() || "";
            return roleLower.includes("data owner") || roleLower.includes("data contributor");
          }
        );

        const list = filtered.map((u) => {
          let parsedLocs = [];
          try {
            // The user's assigned scopes: a sub-location row carries its own id
            // and already reads KMC-T1 in unitCode. Keying it `sub_<id>` keeps it
            // aligned with the scope ids the location picker and scopeOf() use.
            parsedLocs = (u.location || []).map((l) => {
              const scopeId = l.subLocationId ? `sub_${l.subLocationId}` : String(l.sourceId);
              const matched = locations.find((loc) => String(loc.id) === scopeId);
              const resolvedName = matched?.name
                || l.unitCode
                || matched?.location?.unitCode
                || matched?.unitCode
                || l.location?.name
                || `Location ${l.sourceId}`;
              return {
                id: scopeId,
                name: resolvedName,
              };
            });
          } catch (e) {
            parsedLocs = [];
          }
          return {
            id: String(u.userId),
            name: [u.firstName, u.lastName].filter(Boolean).join(" ") +
              (u.designation ? ` (${u.designation})` : ""),
            locations: parsedLocs,
          };
        });
        setAllDataOwners(list);
      }
    } catch {
      setError("Failed to fetch sub-users. Please try again.");
    } finally {
      if (isMounted.current) setLoadingUsers(false);
    }
  };

  // Fetch filtered users who have access to selected locations for Tab 1
  const fetchUsersForLocations = async (locIds) => {
    if (!locIds.length) {
      setUserPool([]);
      return;
    }
    setLoadingUsers(true);
    try {
      // The picker may hold `sub_<id>` keys; this endpoint filters by parent
      // location, so resolve each key to its location id first.
      const parentLocationIds = Array.from(
        new Set(locIds.map((id) => scopeOf(id).locationId).filter(Boolean)),
      );
      const { isSuccess, data } = await apiCall(
        `${config.POSTLOGIN_API_URL_COMPANY}getSubUsersByLocations`,
        {},
        { locationIds: JSON.stringify(parentLocationIds) },
        "GET"
      );
      if (isSuccess && isMounted.current) {
        const incoming = (data?.data || []).map((u) => {
          let userLocIds = [];
          try {
            userLocIds = (u.location || []).map((l) => Number(l.sourceId));
          } catch (e) {
            userLocIds = [];
          }
          return {
            id: String(u.userId),
            name: [u.firstName, u.lastName].filter(Boolean).join(" ") +
              (u.designation ? ` (${u.designation})` : ""),
            locations: userLocIds,
          };
        });
        setUserPool(incoming);
      }
    } catch {
      setError("Failed to filter users by location.");
    } finally {
      if (isMounted.current) setLoadingUsers(false);
    }
  };

  const handleLocationRemove = (id) => {
    const updatedLocs = selectedLocationsScope.filter((l) => l.id !== id);
    setSelectedLocationsScope(updatedLocs);

    const updatedMap = { ...locationUserMap };
    delete updatedMap[id];
    setLocationUserMap(updatedMap);

    const locIds = updatedLocs.map((l) => l.id);
    fetchUsersForLocations(locIds);
  };

  // Handle location-user dropdown change in Tab 1
  const handleLocationUserChange = (locationId, userIds) => {
    setError("");
    setLocationUserMap((prev) => ({
      ...prev,
      [locationId]: userIds,
    }));
  };

  // Assigning a user to a location (or vice-versa) applies to every selected
  // KPI, not just the ones with no owner yet — the system supports multiple
  // owners per KPI+location, so this only ever adds an owner, never replaces
  // one. See the note rendered above each tab.
  const handleSaveLocationTab = async () => {
    setError("");
    const activeCards = locationCards.filter((c) => (locationUserMap[c.id] || []).length);
    if (!activeCards.length) { setError("Select at least one user for a location."); return; }
    setSaving(true);
    try {
      for (const card of activeCards) {
        const scope = scopeOf(card.id);
        await apiCall(`${config.POSTLOGIN_API_URL_COMPANY}assignQuestionScope`, {}, {
          financialYearId,
          questionIds: selectedIds,
          moduleType: "SQ",
          questionnaireType: "CA",
          assignments: locationUserMap[card.id].map((uid) => ({ ...scope, userId: Number(uid) })),
          forceReplace: false,
        }, "POST");
      }
      onSuccess?.(); onHide();
    } catch { setError("Server error. Please try again."); }
    finally { if (isMounted.current) setSaving(false); }
  };

  const handleSaveUserTab = async () => {
    setError("");
    const activeCards = ownerCards.filter((c) => (ownerLocationMap[c.ownerId] || []).length);
    if (!activeCards.length) { setError("Select at least one location for a data owner."); return; }
    setSaving(true);
    try {
      for (const card of activeCards) {
        await apiCall(`${config.POSTLOGIN_API_URL_COMPANY}assignQuestionScope`, {}, {
          financialYearId,
          questionIds: selectedIds,
          moduleType: "SQ",
          questionnaireType: "CA",
          assignments: ownerLocationMap[card.ownerId].map((locId) => ({ ...scopeOf(locId), userId: Number(card.ownerId) })),
          forceReplace: false,
        }, "POST");
      }
      onSuccess?.(); onHide();
    } catch { setError("Server error. Please try again."); }
    finally { if (isMounted.current) setSaving(false); }
  };

  /* ── Remove Data Owner support ──
     Reuses dbAssignments (already fetched for conflict-checking) — one entry
     per selected KPI, each carrying its assignees and the union of locations
     they're assigned across. Unassigning is KPI+user scoped on the backend
     (no per-location granularity), so the location filter only narrows which
     rows are shown — it doesn't limit which of a user's locations get freed. */
  const kpiNameById = useMemo(() => {
    const map = {};
    (selectedKpis || []).forEach((k) => { map[k.id] = k.kpi_name; });
    return map;
  }, [selectedKpis]);

  const questionTitles = kpiNameById; // same shape the drawer expects

  const locationNameById = useMemo(() => {
    const map = {};
    locations.forEach((loc) => {
      map[String(loc.id)] = scopeLabel(loc);
    });
    return map;
  }, [locations, scopeLabel]);

  const usersAtLoc = useCallback((qid, scopeId) => {
    const item = dbAssignments.find((d) => (d.questionId ?? d.assignmentId) === qid);
    if (!item) return [];

    const { locationId, subLocationId } = scopeOf(scopeId);
    return (item.assignees || []).filter((a) => {
      // `scopes` distinguishes KMC-T1 from KMC-T2; older payloads only carry
      // location ids, which can only describe a whole-site assignment.
      if (Array.isArray(a.scopes) && a.scopes.length) {
        return a.scopes.some((s) =>
          Number(s.locationId) === locationId &&
          (s.subLocationId ?? null) === (subLocationId ?? null)
        );
      }
      return subLocationId === null
        && (a.locationIds || a.sourceIds || []).map(Number).includes(locationId);
    });
  }, [dbAssignments, scopeOf]);

  const locsForUser = useCallback((qid, userId) => {
    const item = dbAssignments.find((d) => (d.questionId ?? d.assignmentId) === qid);
    if (!item) return [];
    const a = (item.assignees || []).find((x) => String(x.userId) === String(userId));
    return (a?.locationIds || a?.sourceIds || []).map(Number);
  }, [dbAssignments]);

  const locationCards = useMemo(() => selectedLocationsScope.map((loc) => ({
    ...loc,
    unassignedQids: selectedIds.filter((qid) => usersAtLoc(qid, loc.id).length === 0),
    assignedQids:   selectedIds.filter((qid) => usersAtLoc(qid, loc.id).length > 0),
  })), [selectedLocationsScope, selectedIds, usersAtLoc]);

  const ownerCards = useMemo(() => selectedOwnerIds.map((uid) => {
    const owner = allDataOwners.find((o) => o.id === uid);
    return {
      ownerId: uid,
      owner,
      unassignedQids: selectedIds.filter((qid) => locsForUser(qid, uid).length === 0),
      assignedQids:   selectedIds.filter((qid) => locsForUser(qid, uid).length > 0),
    };
  }), [selectedOwnerIds, allDataOwners, selectedIds, locsForUser]);

  const activeCards = useMemo(() => (
    activeTab === "location"
      ? locationCards.filter((c) => (locationUserMap[c.id] || []).length)
      : ownerCards.filter((c) => (ownerLocationMap[c.ownerId] || []).length)
  ), [activeTab, locationCards, ownerCards, locationUserMap, ownerLocationMap]);

  // Re-derive the open drawer's qIds from the *current* location/owner cards on every
  // render, instead of a snapshot captured at click-time — locationCards/ownerCards
  // already recompute reactively off dbAssignments, so this keeps the drawer's KPI
  // list in sync after a removal inside it triggers onRefreshAssignments (fetchDbAssignments).
  const drawerCard = drawer
    ? (drawer.direction === "location"
        ? locationCards.find((c) => c.id === drawer.scopeId)
        : ownerCards.find((c) => c.ownerId === drawer.scopeId))
    : null;
  const drawerQIds = drawer
    ? (drawer.mode === "unassigned" ? (drawerCard?.unassignedQids || []) : (drawerCard?.assignedQids || []))
    : [];

  if (!show) return null;

  const step1Done = activeTab === "location" ? selectedLocationsScope.length > 0 : selectedOwnerIds.length > 0;
  const step1Label = activeTab === "location" ? "Select Locations" : "Select Data Owners";
  const step2Label = activeTab === "location" ? "Assign Owners per Location" : "Assign Locations per Owner";
  const cards = activeTab === "location" ? locationCards : ownerCards;

  return (
    <>
      <style>{`@keyframes boa-spin { to { transform: rotate(360deg); } }`}</style>

      <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.48)", backdropFilter: "blur(4px)", zIndex: 1200, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
        <div style={{ background: T.white, borderRadius: 16, width: "100%", maxWidth: 600, height: "min(700px, 94vh)", display: "flex", flexDirection: "column", boxShadow: "0 24px 64px rgba(0,0,0,.22)" }}>

          {/* ── Header ── */}
          <div style={{ padding: "16px 20px 14px", borderBottom: `1px solid ${T.rule}`, display: "flex", alignItems: "center", gap: 11, flexShrink: 0 }}>
            <div style={{ width: 40, height: 40, borderRadius: 11, background: T.blueL, border: `1.5px solid ${T.blueB}`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20, flexShrink: 0 }}>👤</div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 15, fontWeight: 800, color: T.ink, letterSpacing: "-0.01em" }}>Assign Data Owners</div>
              <div style={{ fontSize: 11.5, color: T.ink3, marginTop: 2, display: "flex", alignItems: "center", gap: 5 }}>
                <span>{selectedIds.length} KPI{selectedIds.length !== 1 ? "s" : ""} selected</span>
                <span style={{ color: T.rule }}>·</span>
                <span style={{ color: T.blue, fontWeight: 600 }}>{activeTab === "location" ? "By Location" : "By User"}</span>
                {loadingDbAssignments && <><span style={{ color: T.rule }}>·</span><Spin size={10} /></>}
              </div>
            </div>
            <button onClick={onHide} disabled={saving} style={{ border: `1px solid ${T.rule}`, borderRadius: 8, background: T.bg, width: 32, height: 32, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", fontSize: 16, color: T.ink3 }}>×</button>
          </div>

          {/* ── Tabs ── */}
          <div style={{ display: "flex", gap: 4, padding: "10px 20px 0", flexShrink: 0 }}>
            {[
              { key: "location", label: "Assign by Location" },
              { key: "user", label: "Assign by User" },
            ].map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                disabled={saving}
                style={{
                  flex: 1,
                  padding: "9px 14px",
                  fontSize: 12.5,
                  fontWeight: 700,
                  border: "none",
                  background: "none",
                  cursor: saving ? "not-allowed" : "pointer",
                  borderBottom: activeTab === tab.key ? `2.5px solid ${T.blue}` : `2.5px solid ${T.rule2}`,
                  color: activeTab === tab.key ? T.blue : T.ink3,
                  transition: "all .15s",
                }}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {/* ── Body ── */}
          <div style={{ flex: 1, overflowY: "auto", padding: "16px 20px" }}>

            {error && <div style={{ padding: "9px 13px", borderRadius: 8, background: T.redL, border: `1px solid ${T.redB}`, color: T.red, fontSize: 12.5, marginBottom: 14, display: "flex", gap: 8 }}><span>⚠️</span>{error}</div>}

            {skippedDerivedCount > 0 && (
              <div style={{ padding: "9px 13px", borderRadius: 8, background: T.amberL, border: `1px solid ${T.amberB}`, color: T.amber, fontSize: 12, marginBottom: 14, display: "flex", gap: 8 }}>
                <span>👁</span>
                <span>{skippedDerivedCount} derived KPI{skippedDerivedCount !== 1 ? "s" : ""} skipped — derived KPIs are auto-assigned view-only to their sub-question data owners and can't be assigned manually.</span>
              </div>
            )}

            {/* KPIs can have multiple owners — assigning here always applies to every
                selected KPI, including ones that already have an owner. It adds an
                additional owner; it never removes or replaces an existing one. */}
            <div style={{ padding: "9px 13px", borderRadius: 8, background: T.blueL, border: `1px solid ${T.blueB}`, color: T.blue, fontSize: 12, marginBottom: 14, display: "flex", gap: 8 }}>
              <span>ℹ️</span>
              <span>
                {activeTab === "location"
                  ? "Assigning a user to a location applies to every selected KPI there — including ones that already have an owner. This adds the user as an additional owner; it never removes an existing one."
                  : "Assigning a location to this data owner applies to every selected KPI — including ones already owned by someone else. This adds them as an additional owner; it never removes an existing one."}
              </span>
            </div>

            {/* ══ STEP 1 ══ */}
            <StepRow n={1} label={step1Label} done={step1Done} active={!step1Done} />
            <div style={{ marginBottom: 18 }}>
              {activeTab === "location" ? (
                <MultiSelectDropdown
                  options={locations
                    .filter((loc) => loc.assignable !== false)
                    .map((loc) => ({ value: String(loc.id), label: scopeLabel(loc) }))}
                  selected={selectedLocationsScope.map((l) => String(l.id))}
                  onChange={(newLocIds) => {
                    // Keep the picker's own id (numeric for a site, `sub_<id>`
                    // for a sub-location) — Number() would turn KMC-T1 into NaN.
                    const updatedLocs = newLocIds.map((idStr) => {
                      const matched = locations.find((l) => String(l.id) === idStr);
                      return { id: idStr, name: matched ? scopeLabel(matched) : `Location ${idStr}` };
                    });
                    setSelectedLocationsScope(updatedLocs);
                    fetchUsersForLocations(updatedLocs.map((l) => l.id));
                    setLocationUserMap((prev) => {
                      const cleanMap = { ...prev };
                      Object.keys(cleanMap).forEach((locId) => {
                        if (!newLocIds.includes(String(locId))) delete cleanMap[locId];
                      });
                      return cleanMap;
                    });
                  }}
                  placeholder="Select Locations..."
                  searchPlaceholder="Search locations..."
                  showSelectAll={true}
                />
              ) : (
                <MultiSelectDropdown
                  options={allDataOwners.map((owner) => ({ value: owner.id, label: owner.name }))}
                  selected={selectedOwnerIds}
                  onChange={(newSelected) => {
                    setSelectedOwnerIds(newSelected);
                    setOwnerLocationMap((prev) => {
                      const cleanMap = { ...prev };
                      Object.keys(cleanMap).forEach((uid) => {
                        if (!newSelected.includes(uid)) delete cleanMap[uid];
                      });
                      return cleanMap;
                    });
                  }}
                  placeholder="— Choose Data Owners —"
                  searchPlaceholder="Search data owners..."
                  showSelectAll={true}
                />
              )}

              {loadingUsers && (
                <div style={{ marginTop: 8, padding: "7px 11px", borderRadius: 7, background: T.blueL, border: `1px solid ${T.blueB}`, fontSize: 11.5, color: T.blue, display: "flex", alignItems: "center", gap: 7 }}>
                  <Spin size={11} color={T.blue} /> Loading…
                </div>
              )}
            </div>

            {/* ══ STEP 2 ══ */}
            {step1Done && (
              <>
                <StepRow n={2} label={step2Label} done={cards.length > 0 && activeCards.length === cards.length} active={cards.length > 0} />

                {cards.length === 0 && (
                  <div style={{ padding: "11px 14px", borderRadius: 8, background: T.bg, border: `1px solid ${T.rule}`, fontSize: 12, color: T.ink3 }}>
                    {activeTab === "location" ? "Location" : "Owner"} cards will appear here once selected above.
                  </div>
                )}

                {/* TAB 1: LOCATION FIRST */}
                {activeTab === "location" && (
                  <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                    {locationCards.map((card) => {
                      // userPool carries parent location ids; a sub-location card
                      // is eligible for anyone assigned to its site.
                      const eligibleUsers = userPool.filter((u) => u.locations.includes(scopeOf(card.id).locationId));
                      return (
                        <div key={card.id} style={{ border: `1.5px solid ${T.rule}`, borderRadius: 12, background: T.white }}>
                          <div style={{ padding: "10px 14px 9px", background: T.bg, borderBottom: `1px solid ${T.rule}`, borderTopLeftRadius: 12, borderTopRightRadius: 12 }}>
                            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                              <span style={{ fontSize: 13, fontWeight: 700, color: T.ink }}>{card.name}</span>
                              <button onClick={() => handleLocationRemove(card.id)} style={{ border: "none", background: "transparent", color: T.ink4, cursor: "pointer", fontSize: 15 }}>×</button>
                            </div>
                            <div style={{ marginTop: 8, display: "flex", gap: 8, flexWrap: "wrap" }}>
                              <button
                                onClick={() => setDrawer({ direction: "location", scopeId: card.id, scopeLabel: card.name, mode: "unassigned" })}
                                style={{ fontSize: 11, fontWeight: 700, color: T.ink2, background: T.white, border: `1px solid ${T.rule}`, borderRadius: 20, padding: "4px 11px", cursor: "pointer", whiteSpace: "nowrap" }}
                              >
                                Unassigned KPIs ({card.unassignedQids.length}) →
                              </button>
                              {card.assignedQids.length > 0 && (
                                <button
                                  onClick={() => setDrawer({ direction: "location", scopeId: card.id, scopeLabel: card.name, mode: "assignments" })}
                                  style={{ fontSize: 11, fontWeight: 600, color: T.blue, background: T.blueL, border: `1px solid ${T.blueB}`, borderRadius: 20, padding: "4px 11px", cursor: "pointer", whiteSpace: "nowrap" }}
                                >
                                  Assignments ({card.assignedQids.length}) →
                                </button>
                              )}
                            </div>
                          </div>

                          <div style={{ padding: "10px 14px" }}>
                            {card.assignedQids.length > 0 && (
                              <div style={{ marginBottom: 8, fontSize: 11, color: T.ink3 }}>
                                ℹ {card.assignedQids.length} of {selectedIds.length} KPI{card.assignedQids.length !== 1 ? "s" : ""} already {card.assignedQids.length !== 1 ? "have" : "has"} an owner here — users selected below are added as additional owners.
                              </div>
                            )}
                            <MultiSelectDropdown
                              options={eligibleUsers.map((u) => ({ value: String(u.id), label: u.name }))}
                              selected={(locationUserMap[card.id] || []).map(String)}
                              onChange={(vals) => handleLocationUserChange(card.id, vals.map(Number))}
                              placeholder="Select users to assign…"
                              showSelectAll={true}
                              disabled={saving || loadingUsers}
                            />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* TAB 2: USER FIRST */}
                {activeTab === "user" && (
                  <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                    {ownerCards.map((card) => {
                      if (!card.owner) return null;
                      return (
                        <div key={card.ownerId} style={{ border: `1.5px solid ${T.rule}`, borderRadius: 12, background: T.white }}>
                          <div style={{ padding: "10px 14px 9px", background: T.bg, borderBottom: `1px solid ${T.rule}`, borderTopLeftRadius: 12, borderTopRightRadius: 12 }}>
                            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                              <span style={{ fontSize: 13, fontWeight: 700, color: T.ink }}>{card.owner.name}</span>
                              <button
                                onClick={() => {
                                  setSelectedOwnerIds((prev) => prev.filter((id) => id !== card.ownerId));
                                  setOwnerLocationMap((prev) => {
                                    const next = { ...prev };
                                    delete next[card.ownerId];
                                    return next;
                                  });
                                }}
                                style={{ border: "none", background: "transparent", color: T.ink4, cursor: "pointer", fontSize: 15 }}
                              >
                                ×
                              </button>
                            </div>

                            <div style={{ marginTop: 8, display: "flex", gap: 8, flexWrap: "wrap" }}>
                              <button
                                onClick={() => setDrawer({ direction: "user", scopeId: card.ownerId, scopeLabel: card.owner.name, mode: "unassigned" })}
                                style={{ fontSize: 11, fontWeight: 700, color: T.ink2, background: T.white, border: `1px solid ${T.rule}`, borderRadius: 20, padding: "4px 11px", cursor: "pointer", whiteSpace: "nowrap" }}
                              >
                                Unassigned KPIs ({card.unassignedQids.length}) →
                              </button>
                              {card.assignedQids.length > 0 && (
                                <button
                                  onClick={() => setDrawer({ direction: "user", scopeId: card.ownerId, scopeLabel: card.owner.name, mode: "assignments" })}
                                  style={{ fontSize: 11, fontWeight: 600, color: T.blue, background: T.blueL, border: `1px solid ${T.blueB}`, borderRadius: 20, padding: "4px 11px", cursor: "pointer", whiteSpace: "nowrap" }}
                                >
                                  Assignments ({card.assignedQids.length}) →
                                </button>
                              )}
                            </div>
                          </div>

                          <div style={{ padding: "10px 14px" }}>
                            {card.owner.locations.length === 0 ? (
                              <span style={{ fontSize: 12, color: T.red }}>No locations configured for this user.</span>
                            ) : (
                              <>
                                {card.assignedQids.length > 0 && (
                                  <div style={{ marginBottom: 8, fontSize: 11, color: T.ink3 }}>
                                    ℹ {card.assignedQids.length} of {selectedIds.length} KPI{card.assignedQids.length !== 1 ? "s" : ""} already {card.assignedQids.length !== 1 ? "have" : "has"} this owner assigned somewhere — locations selected below are added, not replaced.
                                  </div>
                                )}
                                <MultiSelectDropdown
                                  options={card.owner.locations.map((loc) => ({ value: loc.id, label: loc.name }))}
                                  selected={ownerLocationMap[card.ownerId] || []}
                                  onChange={(newLocSelected) => {
                                    setError("");
                                    setOwnerLocationMap((prev) => ({ ...prev, [card.ownerId]: newLocSelected }));
                                  }}
                                  placeholder="Select locations to assign…"
                                  searchPlaceholder="Search locations..."
                                  showSelectAll={true}
                                  disabled={saving || loadingUsers}
                                />
                              </>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </>
            )}
          </div>

          {/* ── Footer ── */}
          <div style={{ padding: "12px 20px", borderTop: `1px solid ${T.rule}`, flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "space-between", background: T.bg, borderRadius: "0 0 16px 16px" }}>
            <div style={{ fontSize: 11.5, color: T.ink3 }}>
              {activeCards.length > 0 ? `${activeCards.length} card${activeCards.length !== 1 ? "s" : ""} ready` : ""}
            </div>
            <div style={{ display: "flex", gap: 9 }}>
              <button onClick={onHide} disabled={saving} style={{ padding: "8px 18px", borderRadius: 8, border: `1.5px solid ${T.rule}`, background: T.white, color: T.ink2, fontSize: 13, fontWeight: 600, cursor: "pointer" }}>Cancel</button>
              <button
                onClick={activeTab === "location" ? handleSaveLocationTab : handleSaveUserTab}
                disabled={saving || !activeCards.length}
                style={{ padding: "8px 20px", borderRadius: 8, border: "none", background: (saving || !activeCards.length) ? "#93c5fd" : T.blue, color: T.white, fontSize: 13, fontWeight: 700, cursor: (saving || !activeCards.length) ? "not-allowed" : "pointer", display: "flex", alignItems: "center", gap: 7 }}
              >
                {saving ? <><Spin size={12} color={T.white} /> Saving…</> : "Save Changes"}
              </button>
            </div>
          </div>
        </div>
      </div>

      {drawer && (
        <OwnerAssignedKpisDrawer
          direction={drawer.direction}
          scopeId={drawer.scopeId}
          scopeLabel={drawer.scopeLabel}
          qIds={drawerQIds}
          mode={drawer.mode}
          questionTitles={questionTitles}
          dbAssignments={dbAssignments}
          locationNameById={locationNameById}
          allQuestions={allQuestions}
          financialYearId={financialYearId}
          onClose={() => setDrawer(null)}
          onRefreshAssignments={fetchDbAssignments}
        />
      )}
    </>
  );
};

export default BulkOwnerAssignModal;
